'use client'

import dynamic from 'next/dynamic'
import { PlyrProps } from 'plyr-react'
import { useEffect, useEffectEvent, useState } from 'react'

// PlyrLazy bundles plyr's CSS into this lazy chunk, keeping it out of the
// render-blocking CSS for pages containing this section.
const Plyr = dynamic(
  async () => await import('@/ui/components/media/PlyrLazy'),
  {
    ssr: false
  }
)

interface Props {
  videos: any
  poster?: string
}

const Video = ({ videos, poster }: Props) => {
  const [isIOSSafari, setIsIOSSafari] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const onSetIsIOSSafari = useEffectEvent((isIOSSafari: boolean) => {
    setIsIOSSafari(isIOSSafari)
  })

  useEffect(() => {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    const safari =
      /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)
    onSetIsIOSSafari(iOS && safari)
  }, [])

  useEffect(() => {
    if (!isIOSSafari) {
      return
    }

    const handleOrientationChange = () => {
      const isInFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).webkitCurrentFullScreenElement
      )

      if (isInFullscreen) {
        setIsTransitioning(true)

        setTimeout(() => {
          setIsTransitioning(false)
        }, 500)
      }
    }

    window.addEventListener('orientationchange', handleOrientationChange)

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange)
    }
  }, [isIOSSafari])

  useEffect(() => {
    if (!isIOSSafari) {
      return
    }

    const handleGlobalClick = (event: Event) => {
      const target = event.target as HTMLElement

      if (
        target?.classList?.contains('plyr__control') &&
        target?.getAttribute('data-plyr') === 'fullscreen'
      ) {
        event.preventDefault()
        event.stopImmediatePropagation()

        const plyrContainer = target.closest('.plyr')
        const iframe = plyrContainer?.querySelector(
          'iframe'
        ) as HTMLIFrameElement

        if (iframe) {
          try {
            if (iframe.requestFullscreen) {
              iframe.requestFullscreen()
            } else if ((iframe as any).webkitRequestFullscreen) {
              ;(iframe as any).webkitRequestFullscreen()
            } else if ((iframe as any).webkitEnterFullscreen) {
              ;(iframe as any).webkitEnterFullscreen()
            } else {
              if (iframe.contentWindow) {
                iframe.contentWindow.postMessage(
                  '{"method":"requestFullscreen"}',
                  '*'
                )
              }
            }
          } catch (error) {
            try {
              if (iframe.contentWindow) {
                iframe.contentWindow.postMessage(
                  '{"method":"requestFullscreen"}',
                  '*'
                )
              }
            } catch (fallbackError) {
              console.error(fallbackError)
            }
          }
        }
      }
    }

    document.addEventListener('click', handleGlobalClick, { capture: true })

    return () => {
      document.removeEventListener('click', handleGlobalClick, {
        capture: true
      })
    }
  }, [isIOSSafari])

  const plyrProps: PlyrProps = {
    source: {
      type: 'video',
      sources: videos,
      tracks: []
    },
    poster: poster,
    options: {
      controls: [
        'play',
        'progress',
        'current-time',
        'mute',
        'volume',
        'fullscreen'
      ],
      settings: ['quality', 'speed'],
      captions: { active: false, update: false, language: 'auto' },
      clickToPlay: true,
      volume: 0.5,
      resetOnEnd: true,
      fullscreen: {
        enabled: true,
        fallback: true,
        iosNative: true
      },
      ...(isIOSSafari ? {} : ({ playsinline: true } as any))
    } as any
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        opacity: isTransitioning ? 0 : 1,
        transition: 'opacity 0.2s ease'
      }}
    >
      <Plyr {...plyrProps} />
    </div>
  )
}

export default Video
